<template>
  <div style="width: calc(100%); position: relative; aspect-ratio: 16/9;">
    <div
      @mouseover="SET_SCROLL_ENABLED(true)"
      @mouseout="SET_SCROLL_ENABLED(false)"
      id="io-player"
      ref="player"
    ></div>
    <div
      v-if="show_time_overlay"
      style="position: absolute; top: 10px; left: 10px; font-size: 20px; background-color: white; border-radius: 5px; padding: 0 30px; margin-left: calc(50% - 50px)"
    >
      {{ secondsToPretty(currentTime) }}
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Clappr, {} from 'clappr'
import time from '@/utils/player/time'
import Vue from 'vue'

export default {
  props: ['options', 'filter'],
  mixins: [time],
  created() {
    // keyboard listeners
    window.addEventListener('keyup', this.keyUp)
  },
  mounted() {
    this.initPlayer(this.options)
    this.player.attachTo(this.$refs.player)

    this.logPlayerState('PLAYER_CREATED')

    const self = this

    // ---- MediaControl visibility (unchanged) ----
    this.player.listenTo(
      this.player.core.mediaControl,
      Clappr.Events.MEDIACONTROL_HIDE,
      function () {
        self.SET_MEDIACONTROL_VISIBLE(false)
      }
    )

    this.player.listenTo(
      this.player.core.mediaControl,
      Clappr.Events.MEDIACONTROL_SHOW,
      function () {
        self.SET_MEDIACONTROL_VISIBLE(true)
      }
    )

    // ---- High-level Player events (fire on player) ----
    this.player.on(Clappr.Events.PLAYER_SEEK, () => {
      // Only track seek time if player is currently playing
      this.logPlayerState('PLAYER_SEEK')
    })

    this.player.on(Clappr.Events.PLAYER_PLAY, () => {
      this.logPlayerState('PLAYER_PLAY')
    })

    this.player.on(Clappr.Events.PLAYER_STOP, () => {
      this.logPlayerState('PLAYER_STOP')
    })

    // Simple buffering event binding - just like in the store
    Vue.nextTick(() => {
      this.player.listenTo(this.player.core.getCurrentContainer(),
        Clappr.Events.CONTAINER_STATE_BUFFERING,
        () => {
          this.logPlayerState('BUFFER_START')
        })

      this.player.listenTo(this.player.core.getCurrentContainer(),
        Clappr.Events.CONTAINER_STATE_BUFFERFULL,
        () => {
          this.logPlayerState('BUFFER_END')
        })
    })


  },
  shortcuts: {
    keydown: function (e) {
      if (e.keyCode == 16) this.SET_SLOW_SCROLL(true)
      return this.keybinds
    },
    toggle: function () {
      this.toggle()
    },
    skipForward: function () {
      this.skip(5)
    },
    skipForwardLarge: function () {
      this.skip(30)
    },
    skipForwardSmall: function () {
      this.skip(1)
    },
    skipBackward: function () {
      this.skip(-5)
    },
    skipBackwardLarge: function () {
      this.skip(-30)
    },
    skipBackwardSmall: function () {
      this.skip(-1)
    },
    rateUp: function () {
      if (!this.filter) this.playbackRateUp()
      else this.$emit('last')
    },
    rateDown: function () {
      if (!this.filter) this.playbackRateDown()
      else this.$emit('next')
    },
  },
  computed: {
    ...mapGetters('player', [
      'player',
      'show_time_overlay',
      'currentTime',
      'slow_scroll',
      'playing',
      'buffering'
    ]),
    ...mapGetters(['keybinds']),
  },
  methods: {
    ...mapActions('player', [
      'initPlayer',
      'toggle',
      'skip',
      'playbackRateUp',
      'playbackRateDown',
      'changePlaybackRate',
      'destroy',
      'handleScroll',
    ]),
    ...mapMutations('player', [
      'SET_MEDIACONTROL_VISIBLE',
      'SET_SCROLL_ENABLED',
      'SET_SLOW_SCROLL',
    ]),
    keyUp(e) {
      if (e.keyCode == 16) this.SET_SLOW_SCROLL(false)
    },
    logPlayerState(eventText) {
      const playerId = this.player ? this.player.uniqueId || 'unknown' : 'unknown'

      const unixTime = Date.now()

      this.track('Player event', {
        event: eventText,
        player_id: playerId,
        unix_time: unixTime,
        player_playing: this.playing
      })
    },
  },
  beforeDestroy() {
    console.log('[Player] destroyed')
    this.changePlaybackRate(1)

    // Clean up event listeners bound via on/listenTo
    if (this.player) {
      this.player.stopListening && this.player.stopListening()
      this.player.off && this.player.off()
    }

    this.destroy()

    // Window listeners cleanup
    window.removeEventListener('keyup', this.keyUp)
    window.removeEventListener('scroll', this.handleScroll, true)
  },
}
</script>

<style lang="scss" scoped>
#io-player {
  aspect-ratio: 16/9;
  width: 100% !important;
  min-height: 100px;
  z-index: 100;
}
</style>
